(ns pfo.chat.model
  (:require [clojure.core :as c]
            [clojure.string]
            [ajax.json]
            [pfo.api :as api]
            [pfo.util :as util]
            [pfo.auth.model :as auth-model]))

(def llm "llm")
(def system "system")
(def user "user")

(def tool-pending "pending_confirmation")
(def tool-confirmed "confirmed")
(def tool-denied "denied")
(def tool-confirmed-changed "confirmed_changed")
(def tool-create-collection "create_collection")
(def tool-generate-document-summary "generate_document_summary")
(def tool-document-summary-result "document_summary_result")

(def aark-platform-desktop "desktop")
(def aark-platform-mobile "mobile")

(defn chat-init [conversation-id]
  (api/ajaxp  "/chat/init" {:method :post
                            :body (util/make-params
                                   (util/remove-nil-values {:conversation_id conversation-id
                                                            :platform aark-platform-desktop}))}))

(defn message-send [token message upload-ids]
  (api/ajaxp "/chat/message" {:method :post
                              :body (util/make-params
                                     (util/remove-nil-values {:token token
                                                              :content message
                                                              :upload_ids upload-ids}))}))

(defn upload-file [form-data]
  (api/ajaxp "/file/upload/" {:method :post
                              :body form-data}))

(defn conversations []
  (api/ajaxp "/chat/conversations" {:disable-loadin-counter true}))

(defn chat-messages [conversation-id]
  (api/ajaxp (str "/chat/messages/" conversation-id) {}))

(def feedback-reasons
  "Thumbs-down reasons of #3029: server value and label."
  [["wrong_information" "Wrong information"]
   ["incomplete" "Incomplete"]
   ["too_slow" "Took too long"]
   ["did_not_follow_instructions" "Didn't follow instructions"]
   ["formatting" "Formatting issue"]
   ["other" "Other"]])

(defn ratable?
  "An AARK reply the user can rate with thumbs up/down (#3029). The streamed
  placeholder has a string id and is skipped until the saved reply replaces it."
  [message-obj]
  (and (= (:sender message-obj) llm)
       (= (:type message-obj) "text")
       (not (:stopped message-obj))
       (number? (:id message-obj))
       (not (clojure.string/blank? (:content message-obj)))))

(defn feedback-save [message-id feedback]
  (api/ajaxp (str "/chat/feedback/" message-id)
             {:method :post
              :format (ajax.json/json-request-format)
              :params feedback
              :disable-timestamp true
              ;; The popover's "Sending…" button shows progress; no page-wide spinner.
              :disable-loadin-counter true}))

(defn feedback-remove [message-id]
  (api/ajaxp (str "/chat/feedback/" message-id) {:method :delete}))

(defn is-intermediate [message]
  false
  #_(boolean
     (and
      (seq (:tool_calls message))
      (not (some #(= "suggest_responses" (:tool_name %)) (:tool_calls message))))))

(defn chat-message-to-ui-message [message]
  {:sender (:sender message)
   :message (:content message)
   :id (:id message)
   :message-obj message
   :is-intermediate (is-intermediate message)
   :position "single"
   :sentTime (:created_at message)
   :direction (condp = (:sender message)
                llm "incoming"
                system "incoming"
                "outgoing")
   :files (vec (:files message))})

(defn awaits-confirmation [chat-state]
  (= (get-in (last (:messages chat-state)) [:message-obj :type]) "confirmation"))

(defn family-locked?
  "The server refuses a family change once the chat has a user message (#3113). A
   send in flight counts: its message reaches :messages only once stored. A started
   chat without a family may get one only for a single-subscriber user. A change in
   flight locks too, so two changes cannot overlap."
  [chat-state multi-subscriber?]
  (let [started? (or (:loading? chat-state)
                     (some #(= (:sender %) user) (:messages chat-state)))]
    (boolean (or (:family-change-pending? chat-state)
                 (and started?
                      (or (:selected-family-id chat-state) multi-subscriber?))))))

(def ^:private decided-statuses #{tool-confirmed tool-denied tool-confirmed-changed})

(defn- card-decided?
  "A confirmation card whose named calls all carry a decision. A card that names
   no call holds none."
  [card message-objs]
  (let [ids (set (get-in card [:meta :tool_call_ids]))
        named (filter #(contains? ids (:id %)) (mapcat :tool_calls message-objs))]
    (boolean (and (= (:type card) "confirmation")
                  (seq named)
                  (every? #(contains? decided-statuses (:status %)) named)))))

(defn decision-awaits-reply?
  "The card stays the last message until the reply to the click lands, and the
   calls it names then carry their status. The turn runs, so Stop applies."
  [message-objs]
  (card-decided? (last message-objs) message-objs))

(defn stop-guarded? [chat-state]
  (let [message-objs (map :message-obj (:messages chat-state))]
    (or (and (awaits-confirmation chat-state)
             (not (decision-awaits-reply? message-objs)))
        (= (:type (last message-objs)) "suggested_responses"))))

(defn step-base-after-reply-start
  "The server counts steps per model call, and a turn runs several calls. A
   marker of the turn already on screen keeps the steps shown so far as the base
   for the next call. A new turn, or a turn the server could not name, starts at 0."
  [turn-seen turn-step payload-turn]
  (if (and payload-turn (pos? payload-turn) (= payload-turn turn-seen))
    (or turn-step 0)
    0))

(defn accept-turn-payload?
  "A streamed payload carries the id of the user message its turn belongs to. A
   turn below the newest one this browser saw is the stopped turn still writing
   under the next one. A turn stopped from this browser is silent at once. Zero
   or nil means the server could not name the turn."
  [turn-seen stopped-turns payload-turn]
  (cond
    (or (nil? payload-turn) (zero? payload-turn)) true
    (< payload-turn (or turn-seen 0)) false
    (contains? (or stopped-turns #{}) payload-turn) false
    :else true))

(def typing-label "AARK™ is typing…")

(defn turn-state-label
  "The indicator text for the state the server last named. No tool name
  reaches the screen, only the class of the step."
  [{:keys [turn-state turn-step turn-failed?]}]
  (case turn-state
    "thinking" "Thinking…"
    "gathering" "Gathering information…"
    "preparing" "Preparing an action for your approval…"
    "working" "Working…"
    "writing_code" (str "Preparing step " turn-step "…")
    "running_code" (str "Running step " turn-step "…")
    "step_done" (if turn-failed?
                  (str "Adjusting step " turn-step "…")
                  (str "Step " turn-step " done"))
    typing-label))

;; Above the longest time measured in each silent state. A container step
;; ran 75 s, a thinking phase 102 s. "composing" is not measured; it takes the
;; limit of "preparing", which prompt_user had before #3122.
(def slow-thresholds-s {"running_code" 90
                        "working" 120
                        "thinking" 120
                        "gathering" 120
                        "preparing" 120
                        "composing" 120})

(defn slow-turn?
  "The current state has run past the longest time measured for it. The
  seconds come from the server heartbeat, so no browser clock is needed."
  [{:keys [turn-state turn-elapsed]}]
  (boolean
   (when-let [limit (get slow-thresholds-s turn-state)]
     (>= (or turn-elapsed 0) limit))))

(defn is-system-error [message-obj]
  (and (= (:sender message-obj) system) (= (:type message-obj) "error")))

(defn is-stopped [message-obj]
  (and (= (:sender message-obj) system) (= (:type message-obj) "stopped")))

(def unrecoverable-error-codes
  "Error codes that end the chat when they arrive over SSE. The server sends prompt_too_long
  only when it has nothing left to drop from the request, so a new one cannot be retried. The
  same code loaded from the database can be older than that -- see error-type below."
  #{"prompt_too_long"})

(def context-limit-message
  "Shown when an error loaded from the database says the request was too long. The server now
  trims an oversized tool result out of the request, so a retry can work. A chat that is too
  long for other reasons fails again, and that new failure then ends it."
  (str "This conversation reached the context limit set by the LLM. Try again. If the error "
       "repeats, start a new conversation and work with the documents in smaller pieces there."))

(def locked-earlier-message
  (str "An earlier attempt to continue this chat failed. The chat has been repaired since. "
       "Try again. If the error repeats, start a new chat."))

(def novice-locked-message
  "The queue worker stored only the code, so the copy lives here. Mirrors the server's
  noviceDowngradeMessage — deliberately not naming the Novice setting (#3106)."
  "This conversation is read-only. Please start a new conversation.")

;; Codes with a banner of their own, more specific than the :reply-failed one (#3105).
(def coded-error-types
  {"response_cut_off"        :cut-off
   "refusal"                 :refused
   "pause_turn"              :paused
   "context_window_exceeded" :context-overflow
   "service_overloaded"      :overloaded
   "unsupported_file_format" :unsupported-file
   ;; The queue worker refused a message queued before the conversation was
   ;; locked read-only (#3106); a retry meets the same refusal.
   "novice_locked"           :novice-locked})

(def turn-ran-error-types
  "The message reached the model and the turn ran, so the bubble is not a send failure."
  #{:timeout :cut-off :refused :paused :context-overflow :decided :reply-failed})

(def no-resend-error-types
  "An identical request meets the same limit again, so nothing goes back into the composer.
  The other turn-ran types ask the user to rephrase or to try again, and the text helps there."
  #{:timeout :cut-off :decided})

;; live? tells two cases apart. An error arriving over SSE ended a request that the
;; server had already trimmed, so there is nothing left to try. An error loaded from
;; the database can be older than that server change, so a retry can work (#3079).
(defn error-type [error-code live?]
  (cond
    (= error-code "ipaladin_error")                       :service-interruption
    (= error-code "timeout_error")                        :timeout
    ;; The text validateConversation stored, before it learned to repair the
    ;; history instead of refusing it. Only old rows carry it, and migration
    ;; 0298 repaired their conversations, so a retry works now (#3105).
    (= error-code "conversation has inconsistent state") :locked-earlier
    (coded-error-types error-code)                        (coded-error-types error-code)
    (unrecoverable-error-codes error-code)                (if live? :unrecoverable :context-limit)
    ;; The server sent the code, so the message arrived. :transient is a failed send (#3117).
    :else                                                 :reply-failed))

(defn decided-card-in-turn?
  "A decided card between the user message and the error. The decision took
   effect, so the error is no failed send."
  [message-objs user-message-id error-id]
  (boolean
   (some #(and (> (:id %) user-message-id)
               (< (:id %) error-id)
               (card-decided? % message-objs))
         message-objs)))

(defn error-type-after-decision
  "Resending the prompt after a decision proposes the action again. A limit
   that ends the chat keeps its own type."
  [error-type decided?]
  (if (and decided? (not (#{:unrecoverable :context-limit} error-type)))
    :decided
    error-type))

(defn fail-turn
  "Records a system error that arrived over SSE against the user message it answers.

  A timed-out or cut-off message reached the model and was worked on, so nothing of it goes
  back into the composer, neither the text nor the attachments. Handing either one back
  rebuilds the request that just exceeded the budget, and the documents are its heavy half.

  The type is stored next to the flag rather than in place of it: the renderer needs it to
  withhold send-failure styling from a turn that ran. mark-historical-errors rebuilds the
  same pair from server data when the conversation is reloaded."
  [state error-message]
  (let [parent-id   (:parent_message_id error-message)
        banner-type (error-type-after-decision
                     (error-type (:content error-message) true)
                     (decided-card-in-turn? (map :message-obj (:messages state)) parent-id (:id error-message)))
        no-resend   (no-resend-error-types banner-type)
        messages    (mapv (fn [msg]
                            (if (= (:id msg) parent-id)
                              (assoc msg :error true :error-type banner-type)
                              msg))
                          (:messages state))]
    (-> state
        (assoc :messages messages)
        (assoc :restore-input (when-not no-resend (:pending-message state)))
        (assoc :restore-files (when-not no-resend (:pending-files state)))
        (assoc :pending-message nil)
        (assoc :pending-files nil)
        (assoc :input-error {:type banner-type}))))

(defn mark-historical-errors
  "Sets the same :error / :error-type pair as fail-turn, for a conversation arriving from
  the server rather than over SSE."
  [messages]
  (let [message-objs (map :message-obj messages)
        error-type-by-parent (->> message-objs
                                  (filter is-system-error)
                                  (reduce (fn [acc m]
                                            (assoc acc
                                                   (:parent_message_id m)
                                                   (error-type-after-decision
                                                    (error-type (:content m) false)
                                                    (decided-card-in-turn? message-objs (:parent_message_id m) (:id m)))))
                                          {}))]
    (mapv (fn [msg]
            (if-let [banner-type (get error-type-by-parent (:id msg))]
              (assoc msg :error true :error-type banner-type)
              msg))
          messages)))

;; Banner text for the types that need no action button. The types with one
;; (a new chat) stay in the component, next to the action.
(def banner-copy
  {:cut-off          {:theme       :warning
                      :title       "Response too long"
                      :description (str "AARK™'s reply exceeded the maximum length and nothing from it was saved. "
                                        "Ask for a smaller part of the request — a shorter report, "
                                        "or one account at a time.")}
   :refused          {:theme       :warning
                      :title       "Response not completed"
                      :description (str "AARK™ stopped this reply before it was complete. "
                                        "Rephrase the request, or start a new chat.")}
   :paused           {:theme       :warning
                      :title       "Task paused"
                      :description (str "AARK™ paused a long-running task before it finished. "
                                        "Ask again to continue the work.")}
   :context-overflow {:theme       :warning
                      :title       "Request too large"
                      :description (str "The request exceeded the context limit set by the LLM. Try again. "
                                        "If the error repeats, remove one or more documents "
                                        "or reduce the scope of your query.")}
   :overloaded       {:theme       :warning
                      :title       "Service busy"
                      :description (str "AARK™ is unavailable due to high demand or rate limits. "
                                        "Try again in a few moments.")}
   :unsupported-file {:theme       :warning
                      :title       "Unsupported file"
                      :description "The file format you uploaded is not supported."}
   :reply-failed     {:theme       :warning
                      :title       "AARK™ could not finish this reply"
                      :description (str "Your message was received, but an error stopped AARK™ before it could answer. "
                                        "Please try again. If the error repeats, start a new chat.")}
   ;; The decision took effect in the request. Only the reply to it failed, so
   ;; asking again would propose the same action a second time.
   :decided          {:theme       :warning
                      :title       "Your decision was recorded, but the reply failed"
                      :description (str "AARK™ acted on your decision but could not finish its reply. "
                                        "Check the result before you ask again.")}})
